<script>
    import {onMount} from "svelte";
    import {derived, writable} from 'svelte/store';

    import { session, isAdmin, getUsers } from './Utilities/Session.svelte';

    import {db, offlineDatabases} from './Database.svelte';
    import {confirm} from './Components/Confirmation.svelte';
    import FormModal from './Forms/FormModal.svelte';
    import {addFormModal} from './Forms/ModalFormManager.svelte';
    import Loader from './Components/Loader.svelte';
    import {notifier} from "@beyonk/svelte-notifications"

    import SortableTable from './Components/SortableTable.svelte';
    import {tables} from './lib/tables';
    import SyncStatus from './Components/Assessment/SyncStatus.svelte';

    import {sortBy, groupBy} from 'lodash-es';

    import Assessment from './Models/assessmentModel';
    import AssessmentTypeModel from './Models/assessmentTypeModel';

    let assessments = Assessment.getAssessmentList();

    let assessment_types = AssessmentTypeModel.getAssessmentTypeList();
    let showFormModal;

    let user_choices;

    let show_all = writable(false);

    let sortFields = [
        ...tables["assessmentList"],
        {
            label: "Sync Status",
            field: "assessment_db",
            type: "checkbox",
            component: SyncStatus,
            filterFunc: (filter, item) => {
                return Boolean($offlineDatabases[item.assessment_db]) == filter;
            }
        }
    ]

    async function addAssessment() {
        let choices = $assessment_types.map((type) => {
            return {
                label: type.name,
                value: type._id,
                production: type.production
            }
        });

        choices = groupBy(choices, (choice) => !!choice.production);

        let fields = [{
            name: "name",
            placeholder: "Assessment Name",
            required: true,
        },
        {
            name: "matter",
            placeholder: "Matter Number",
        },
        {
            name: "type",
            type: "select",
            dflt: sortBy(choices[true], ['label'])[0].value,
            required: true,
            choices: {
                static: {
                    groups: [
                        {
                            name: "Production",
                            options: sortBy(choices[true], ['label'])
                        },
                        {
                            name: "Draft",
                            options: sortBy(choices[false], ['label'])
                        }
                    ]
                }
            }
        },
        {
            name: "users",
            type: "select",
            required: true,
            multiple: true,
            dflt: [$session.user.email],
            choices: {
                static: user_choices
            }
        }];

        showFormModal = {
            fields: fields,
            title: "Create Assessment",
            cancelAction: () => showFormModal = undefined,
            saveAction: saveAssessment
        }
    }

    function saveAssessment(assessment) {
        Assessment.createAssessment(assessment).then(() => {
            notifier.success("Assessment Created");
        }).catch(e => {
            console.error(e)
            notifier.danger(e);
        });
        showFormModal = undefined;

    }

    function deleteAssessment(assessment) {
        confirm(`Are you sure you which to delete "${assessment.name}"? All data will be deleted and this cannot be undone.`).then(async () => {
            stopSync(assessment);
            await Assessment.deleteAssessment(assessment);
            notifier.success("Assessment Deleted");
        }).catch((e) => notifier.danger(e));
    }

    function updateAssessment(assessment) {
        addFormModal({
            fields: [
                {
                    name: "name",
                    label: "Assessment Name",
                    required: true
                }, {
                    name: "matter",
                    label: "Mater Number",
                    required: false
                }
            ],
            data: assessment
        }).then((results) => {
            Assessment.saveDetails(results).then(() => {
                notifier.success("Assessment Saved")
            });
        }).catch(() => null)
    }

    function backupAssessment(assessment_details) {
        if (!$isAdmin) return;

        let assessment_model = new Assessment(assessment_details);

        let unsub = assessment_model.store.subscribe((assessment) => {
            if (!assessment) return;


            // only do this once
            unsub();

            // remove circular reference
            assessment.model = undefined

            let promises = [];
            Object.values(assessment.evidence).forEach((evidence) => {
                evidence.forEach(attachment => {
                    promises.push(db.get(attachment._id, {attachments: true}));
                })
            });
            Promise.all(promises).then((attachments) => {

                assessment.attachments = {};
                attachments.forEach(att => {
                    if (att.attachment_id)
                        assessment.attachments[att.attachment_id] = att._attachments[att.attachment_id];
                })

                const blob = new Blob([JSON.stringify(assessment)], {type : 'application/json'});
                const url = URL.createObjectURL(blob);

                let a = document.createElement("a");
                a.download = `${assessment.details.name}.json`;
                a.href = url;
                a.click();

                URL.revokeObjectURL(url);
            });
        })
    }

    let filtered_list = derived([assessments, session, show_all], ([$assessments, $sess, $showAll], set) => {
        if (!$assessments || !$sess) {
            return;
        }

        if ($showAll) {
            set($assessments);
        } else {
            set($assessments.filter(a => !a.users || a.users.includes($sess.user.email)));
        }

    })

    function stopSync(assessment) {
        if ($offlineDatabases[assessment.assessment_db]){
            if ($offlineDatabases[assessment.assessment_db].syncStatus != 0) {
                confirm("Assessment Sync is not complete, unsynced changes may be lost. Are you sure?").then(() => {
                    Assessment.stopSync(assessment)
                })
            } else {
                Assessment.stopSync(assessment)
            }
        }
    }

    function startSync(assessment) {
        if (Object.keys($offlineDatabases).length >= 5) {
            notifier.danger("Cannot sync more than 5 assessments");
            return;
        }

        Assessment.startSync(assessment)
    }

    onMount(async () => {
        user_choices = await getUsers();
    })
</script>

<div class="container is-fluid">
    <div class="columns">
        <div class="column">
            <h2 class="title is-3">Assessments</h2>
            <Loader loading={!$filtered_list}>
                <SortableTable
                    sortFields={sortFields}
                    tableData={$filtered_list}
                    actionsWidth="{$isAdmin ? 390 : 330}"
                    defaultSort="create_date"
                    showHideFields={false}
                >
                    <div slot="rowActions" let:row={assessment}>
                        <div class="buttons is-right">
                            {#if $isAdmin || assessment.users.includes($session.user.email) }
                                {#if $isAdmin}
                                    <button class="button" on:click={() => backupAssessment(assessment)}>Export</button>
                                {/if}
                                <button class="button is-info" on:click={() => updateAssessment(assessment)}>Edit</button>
                                <button class="button is-danger" on:click={() => deleteAssessment(assessment)}>Delete</button>
                                {#if assessment.assessment_db}
                                    {#if $offlineDatabases[assessment.assessment_db]}
                                        {#if $offlineDatabases[assessment.assessment_db].syncStatus != 0}
                                            <span class="button is-warning">Syncing...</span>
                                        {:else}
                                            <span class="button is-info" on:click={() => stopSync(assessment)}>Stop Sync</span>
                                        {/if}
                                    {:else}
                                        <span class="button is-success" on:click={() => startSync(assessment)}>Start Sync</span>
                                    {/if}
                                {/if}
                                <!--<a class="button is-link" href="#/assessment/{assessment._id}/scope">Scoping</a>-->
                                <a class="button is-link" href="#/assessment/{assessment._id}">Open</a>
                            {:else}
                                <span>View Only</span>
                            {/if}
                        </div>
                    </div>
                    <div slot="filterActions">
                        <div class="buttons is-right">
                            <button class="button is-success is-right" on:click={addAssessment}>Create New Assessment</button>
                        </div>
                    </div>
                    <div slot="footerActions">
                        <label class="is-pulled-left">
                            Show All Assessments
                            <input type="checkbox" bind:checked="{$show_all}">
                        </label>
                        <div class="buttons is-right">
                            <button class="button is-success is-right" on:click={addAssessment}>Create New Assessment</button>
                        </div>
                    </div>
                </SortableTable>
            </Loader>
        </div>
        <div class="column is-narrow">
            <img src="images/Katana Cat.jpg" alt="Mascot">
        </div>
    </div>
</div>

{#if showFormModal}
    <FormModal {...showFormModal}></FormModal>
{/if}

<style>
    .container {
        padding-left: 32px;
        padding-right: 32px;
    }
</style>